'use client'

import { ComponentProps, useContext, useMemo } from 'react'

import { OfferRequestMessage as BaseOfferRequestMessage } from '@marketplace-web/offers/price-offers-feature'
import {
  ConversationListContext,
  useConversationContext,
} from '@marketplace-web/messaging/conversation-providers-feature'

import {
  ApplicableTransactionAction,
  useNewConversation,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { invariant } from '@marketplace-web/messaging/messaging-util'
import { useSession } from '@marketplace-web/shared/session-data'

import useTransactionActions from './useTransactionActions'

type Props = Pick<
  ComponentProps<typeof BaseOfferRequestMessage>,
  'message' | 'isGrouped' | 'createdTime' | 'entityType'
>

const InquiryOfferRequestMessage = (props: Props) => {
  const { getConversationRequest, conversation: oldConversation } = useConversationContext()
  const { updateConversation } = useContext(ConversationListContext)
  const { user } = useSession()

  const conversation = useNewConversation(oldConversation!.id)

  // this weird check ensures that the `data` field is reduced to the inquiry conversation
  invariant(
    'items' in conversation.data,
    // TODO: replace with proper error class
    new Error('UnexpectedConversationTypeResponse: expected inquiry conversation type'),
  )

  const [item] = conversation.data.items
  invariant(
    item,
    // TODO: replace with proper error class
    new Error('InquiryConversationWithoutItems'),
  )

  const [oppositeUser] = conversation.oppositeUsers

  invariant(
    oppositeUser,
    // TODO: replace with proper error class
    new Error('InquiryConversationMissingOppositeUser'),
  )

  const transformedOppositeUser = useMemo(
    () => ({
      ...oppositeUser,
      city: null,
      localCountryTitle: '',
      isLocationExposed: null,
      login: oppositeUser.loginName,
      feedbackCount: null,
      feedbackReputation: 0,
      isModerator: false,
      isUserUnblockable: false,
      isSystem: false,
      photoThumbnail: oppositeUser.photo?.thumbnails?.[0]?.url ?? null,
      photo: null,
    }),
    [oppositeUser],
  )

  const transactionId = conversation.data.transactionId as unknown as number
  const itemId = item.inquiryItemId as unknown as number

  const transactionActions = useTransactionActions(itemId, conversation.data.currentUserSide)
  const buyerId = conversation.data.currentUserSide === 'buyer' ? user?.id : Number(oppositeUser.id)

  return (
    <BaseOfferRequestMessage
      {...props}
      transactionId={transactionId}
      // TODO: fix opposite user type mismatch
      oppositeUser={transformedOppositeUser}
      offerPrice={conversation.data.inquiryValue}
      applicableTransactionActions={transactionActions}
      isPaymentActionAvailable={transactionActions.includes(ApplicableTransactionAction.Buy)}
      // TODO: somehow resolve seller countdown
      isSellerCountdownActive={false}
      buyerId={buyerId}
      onActionClick={() => {
        getConversationRequest({ conversationId: conversation.id })

        updateConversation({ id: conversation.id })
      }}
    />
  )
}

export default InquiryOfferRequestMessage
